import { ReactNode, useEffect, useRef } from 'react';
import type {
  AnyFieldSpecBase,
  ScopeSection as SchemaSection
} from 'v5/core/form-schema/formSchema.types';

type ScopeSection = SchemaSection<AnyFieldSpecBase>;
import { Tabs, TabsList, TabsTrigger } from 'v5/design-system/Tabs';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { OverriddenCountBadge } from './OverriddenCountBadge';

export interface SectionTabsProps {
  sections: readonly ScopeSection[];
  /** Highlighted tab: the section in view. */
  activeId: string | null;
  onSelect(sectionId: string): void;
  renderIcon?(section: ScopeSection): ReactNode;
  className?: string;
}

const TAB_ATTRIBUTE = 'data-section-tab';

/**
 * One tab per scope section (Global, each workspace). Selecting a tab scrolls
 * to that section; the tab of the section in view — or the one the user is
 * at, under the pointer or holding the focus — is highlighted. A grey strip
 * across the form edge to edge (see `SCHEMA_FORM_GUTTER`), sticky at the top
 * of the host's scroll area or above the form's own (`scroll="own"`); its top
 * padding is its own, so it sits at the same place at rest and when stuck.
 * The first and last tab keep the gutter as margins, lining up with the
 * sections until the strip scrolls. With few sections the tabs share the
 * width; with many (10 workspaces) they keep their natural width and the
 * strip scrolls horizontally, keeping the active tab in view.
 */
export function SectionTabs({
  sections,
  activeId,
  onSelect,
  renderIcon,
  className
}: SectionTabsProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Keep the active tab visible when the strip overflows: clear of the faded
  // edges and their scroll buttons, which the scroller's scroll padding covers.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || activeId === null) return;
    const trigger = scroller.querySelector<HTMLElement>(
      `[${TAB_ATTRIBUTE}="${activeId.replace(/["\\]/g, '\\$&')}"]`
    );
    if (!trigger) return;
    const strip = scroller.getBoundingClientRect();
    const { scrollPaddingLeft, scrollPaddingRight } =
      getComputedStyle(scroller);
    const visibleLeft = strip.left + (parseFloat(scrollPaddingLeft) || 0);
    const visibleRight = strip.right - (parseFloat(scrollPaddingRight) || 0);
    const tab = trigger.getBoundingClientRect();
    // Include the edge tabs' margins, so the first/last tab scrolls fully back.
    const { marginLeft, marginRight } = getComputedStyle(trigger);
    const tabLeft = tab.left - parseFloat(marginLeft);
    const tabRight = tab.right + parseFloat(marginRight);
    if (tabLeft < visibleLeft) {
      scroller.scrollBy({ left: tabLeft - visibleLeft, behavior: 'smooth' });
    } else if (tabRight > visibleRight) {
      scroller.scrollBy({ left: tabRight - visibleRight, behavior: 'smooth' });
    }
  }, [activeId]);

  return (
    <Tabs
      value={activeId ?? undefined}
      onValueChange={onSelect}
      // Sticky at the top of the host's scroll area, which renders no top
      // shadow of its own (`renderTopShadow={false}`): the bar casts it once
      // content scrolls underneath, like the feature-flag catalog headers.
      // Above a scroll area of the form's own (`scroll="own"`), that area
      // casts it.
      className={cn(
        'sticky top-0 z-20 bg-bg-default pt-3 transition-shadow duration-100',
        '[[data-has-top-shadow]_&]:shadow-sm',
        className
      )}
      data-testid="schema-section-tabs"
    >
      {/*
       * Horizontal scroller without a visible scrollbar, edge to edge; tabs
       * scrolled out of view fade out at the edges, under a chevron that
       * scrolls on (the fade alone can land on the gap between two tabs). The
       * grey strip background sits on a parent, as the fade mask would fade
       * the scroller's own. The list's vertical padding equals the triggers'
       * focus ring (2px ring + 2px offset, in px: the spacing scale is not
       * 4px), so the scroller never clips it.
       */}
      <div className="bg-bg-subtle">
        <ScrollShadowWrapper
          ref={scrollerRef}
          orientation="horizontal"
          edge="fade"
          scrollButtons
          className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <TabsList
            variant="default"
            className="flex w-max min-w-full bg-transparent px-0 py-[4px]"
            aria-label="Sections"
          >
            {sections.map(section => (
              <TabsTrigger
                key={section.id}
                value={section.id}
                className="flex-[1_0_auto] gap-2 whitespace-nowrap first:ms-(--schema-form-gutter) last:me-(--schema-form-gutter)"
                data-testid={`schema-section-tab-${section.id}`}
                {...{ [TAB_ATTRIBUTE]: section.id }}
              >
                {renderIcon ? renderIcon(section) : null}
                <span>{section.title}</span>
                {section.overriddenCount > 0 && (
                  <OverriddenCountBadge count={section.overriddenCount}>
                    {section.overriddenCount}
                  </OverriddenCountBadge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </ScrollShadowWrapper>
      </div>
    </Tabs>
  );
}
